import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase,
  Users,
  Clock,
  Trash2,
  Eye,
  Loader2,
  User,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-800' },
  processing: { label: 'İşleniyor', color: 'bg-blue-100 text-blue-800' },
  completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-800' },
};

const workTypeLabels = {
  full_time: 'Tam Zamanlı',
  part_time: 'Yarı Zamanlı',
  temporary: 'Geçici',
  project_based: 'Proje Bazlı'
};

const AdminPersonnelRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('eta_admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/personnel-requests`, {
        headers: getAuthHeaders()
      });
      setRequests(response.data);
    } catch (error) {
      toast.error('Talepler yüklenirken hata oluştu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleStatusChange = async (reqId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/personnel-requests/${reqId}/status?status=${newStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      setRequests(requests.map(req => 
        req.id === reqId ? { ...req, status: newStatus } : req
      ));
      toast.success('Durum güncellendi');
    } catch (error) {
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const handleMarkAsRead = async (reqId) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/personnel-requests/${reqId}/read`,
        {},
        { headers: getAuthHeaders() }
      );
      setRequests(requests.map(req => 
        req.id === reqId ? { ...req, is_read: true } : req
      ));
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (reqId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/personnel-requests/${reqId}`, {
        headers: getAuthHeaders()
      });
      setRequests(requests.filter(req => req.id !== reqId));
      setSelectedRequest(null);
      toast.success('Talep silindi');
    } catch (error) {
      toast.error('Talep silinirken hata oluştu');
    }
  };

  const viewRequest = (req) => {
    setSelectedRequest(req);
    if (!req.is_read) {
      handleMarkAsRead(req.id);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = requests.filter(req => !req.is_read).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-personnel-requests">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Personel Talepleri
            </h1>
            <p className="text-slate-500 mt-1">
              Toplam {requests.length} talep
              {unreadCount > 0 && ` • ${unreadCount} yeni`}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#005CB2] animate-spin" />
          </div>
        ) : requests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Henüz personel talebi yok</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Request List */}
            <div className="space-y-4">
              {requests.map((req) => (
                <Card 
                  key={req.id}
                  data-testid={`personnel-request-${req.id}`}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    !req.is_read ? 'border-l-4 border-l-[#005CB2]' : ''
                  } ${selectedRequest?.id === req.id ? 'ring-2 ring-[#005CB2]' : ''}`}
                  onClick={() => viewRequest(req)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#005CB2]/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-[#005CB2]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                            {req.company_name}
                            {!req.is_read && (
                              <span className="w-2 h-2 bg-[#005CB2] rounded-full" />
                            )}
                          </h3>
                          <p className="text-sm text-slate-500">{req.position} • {req.count} kişi</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {formatDate(req.created_at)}
                          </p>
                        </div>
                      </div>
                      <Badge className={statusConfig[req.status || 'pending'].color}>
                        {statusConfig[req.status || 'pending'].label}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Request Detail */}
            {selectedRequest ? (
              <Card className="sticky top-4" data-testid="personnel-request-detail">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Talep Detayı
                    </CardTitle>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Talebi Sil</AlertDialogTitle>
                          <AlertDialogDescription>
                            Bu talebi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>İptal</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDelete(selectedRequest.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Sil
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* Status */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Durum
                    </label>
                    <Select
                      value={selectedRequest.status || 'pending'}
                      onValueChange={(value) => handleStatusChange(selectedRequest.id, value)}
                    >
                      <SelectTrigger data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(statusConfig).map(([key, { label }]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Company Info */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Firma Adı</p>
                        <p className="font-medium">{selectedRequest.company_name}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Yetkili Kişi</p>
                        <p className="font-medium">{selectedRequest.contact_person}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">E-posta</p>
                        <a href={`mailto:${selectedRequest.email}`} className="font-medium text-[#005CB2] hover:underline">
                          {selectedRequest.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Telefon</p>
                        <a href={`tel:${selectedRequest.phone}`} className="font-medium">
                          {selectedRequest.phone}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Position Details */}
                  <div className="pt-4 border-t space-y-4">
                    <h4 className="font-semibold text-slate-800">Pozisyon Bilgileri</h4>
                    
                    <div className="flex items-center gap-3">
                      <Briefcase className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Pozisyon</p>
                        <p className="font-medium">{selectedRequest.position}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Personel Sayısı</p>
                        <p className="font-medium">{selectedRequest.count} kişi</p>
                      </div>
                    </div>

                    {selectedRequest.work_type && (
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Çalışma Şekli</p>
                          <p className="font-medium">{workTypeLabels[selectedRequest.work_type] || selectedRequest.work_type}</p>
                        </div>
                      </div>
                    )}

                    {selectedRequest.experience_required && (
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Deneyim Gereksinimi</p>
                          <p className="font-medium">{selectedRequest.experience_required}</p>
                        </div>
                      </div>
                    )}

                    {selectedRequest.location && (
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Çalışma Lokasyonu</p>
                          <p className="font-medium">{selectedRequest.location}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {selectedRequest.description && (
                    <div className="pt-4 border-t">
                      <p className="text-sm text-slate-500 mb-2">Açıklama</p>
                      <p className="text-slate-700 whitespace-pre-wrap">{selectedRequest.description}</p>
                    </div>
                  )}

                  {/* Date */}
                  <div className="pt-4 border-t text-sm text-slate-500">
                    <Clock className="w-4 h-4 inline mr-2" />
                    {formatDate(selectedRequest.created_at)}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="flex items-center justify-center min-h-[400px]">
                <CardContent className="text-center">
                  <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Detay görüntülemek için bir talep seçin</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPersonnelRequests;
